/**
 * Largeur réelle d'un élément, suivie au redimensionnement.
 *
 * Existe pour une seule raison : **le graphique de poids est dessiné en pixels
 * réels, pas dans un `viewBox` mis à l'échelle.** Un SVG à `viewBox` fixe étiré
 * par `width: 100%` grossit *tout* avec lui, textes compris : les libellés
 * d'axes passeraient de 10 px sur iPhone à 18 px sur la colonne de 640 px du
 * bureau, et l'épaisseur des traits suivrait. Mesurer d'abord et dessiner
 * ensuite garde une graisse de trait et une taille de texte constantes quelle
 * que soit la largeur — ce qui est aussi ce qui rend vérifiable la promesse
 * « aucun texte ne déborde ».
 *
 * 🔴 **Ref de rappel, et surtout pas une `RefObject`.** Une première version
 * prenait un `useRef` en argument et observait `ref.current` dans un `useEffect`
 * dépendant de `[ref]`. L'objet de `useRef` étant stable, cet effet ne
 * s'exécute qu'**une fois, au montage** : si l'élément mesuré n'est pas encore
 * dans le DOM à cet instant, `ref.current` vaut `null`, aucun observateur n'est
 * posé, et la largeur reste à zéro **définitivement**.
 *
 * C'est exactement ce qui est arrivé : l'écran de poids ne monte le conteneur du
 * graphique qu'à partir de la première pesée, si bien que le graphique ne
 * s'affichait plus jamais après la toute première saisie — jusqu'à un
 * rechargement de la page. Trouvé au banc navigateur, pas au typecheck : le code
 * compile parfaitement, et le défaut ne se voit que sur le parcours « base vide
 * → première pesée », qu'on ne rejoue qu'une fois dans la vie de l'application.
 *
 * Une ref de rappel est appelée par React à **chaque** attachement et
 * détachement du nœud ; l'effet est donc relancé quand l'élément apparaît.
 *
 * ⚠️ La largeur vaut `0` tant que rien n'est mesuré (premier rendu, élément
 * détaché). L'appelant doit traiter ce cas plutôt que de dessiner dans le vide.
 */

import { useEffect, useState } from 'react';

export function useElementWidth(): {
  /** À passer tel quel à `ref={…}` sur l'élément à mesurer. */
  ref: (node: HTMLElement | null) => void;
  width: number;
} {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (node === null) return;

    /*
     * ⚠️ Aucune mesure synchrone ici, et il n'en faut pas : `observe()` déclenche
     * lui-même un premier rappel avec la taille courante de l'élément, avant la
     * peinture. Poser un `setWidth` à côté serait à la fois redondant et un
     * `setState` direct dans un effet, que la règle `react-hooks/
     * set-state-in-effect` refuse à juste titre — c'est le cas qui provoque un
     * rendu de plus sans rien apporter.
     */
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [node]);

  return { ref: setNode, width };
}
